import { Container } from "@mui/material";

import {
  Route,
  RouterProvider,
  Routes,
  ScrollRestoration,
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
} from "react-router-dom";
import "reflect-metadata";
import { container } from "tsyringe";
import "./App.css";
import { Home } from "./features/home/Home";
import { BaseConverter } from "./features/base_converter/BaseConverter";
import { BmiCalculator } from "./features/bmi_calculator/BmiCalculator";
import { CompoundInterestCalculator } from "./features/compound_interest_calculator/CompoundInterestCalculator";
import { CreditCardRepaymentCalculator } from "./features/credit_card_repayment_calculator/CreditCardRepaymentCalculator";
import { DistanceUnitConverter } from "./features/distance_unit_converter/DistanceUnitConverter";
import { FaviconGenerator } from "./features/favicon_generator/FaviconGenerator";
import { TemperatureConverter } from "./features/temperature_converter/TemperatureConverter";
import { WeightUnitConverter } from "./features/weight_unit_converter/WeightUnitConverter";

import { AnalyticsService } from "./services/analytics";
import { AndroidIosImageResizer } from "./features/android_ios_image_resizer/AndroidIosImageResizer";
import { PdfMerger } from "./features/pdf_merger/PdfMerger";
import { AboutUs } from "./features/about_us/AboutUs";
import { ImageConverter } from "./features/image_converter/ImageConverter";
import { Compression } from "./features/compression/Compression";
import { QrCodeGenerator } from "./features/qr_code_generator/QrCodeGenerator";
import { PasswordGenerator } from "./features/password_generator/PasswordGenerator";
import { LoremIpsumGenerator } from "./features/lorem_ipsum_generator/LoremIpsumGenerator";
import { BarcodeGenerator } from "./features/barcode_generator/BarcodeGenerator";
import { CsvJsonConverter } from "./features/csv_json_converter/CsvJsonConverter";
import { ImageCropper } from "./features/image_cropper/ImageCropper";
import { EmailSignatureGenerator } from "./features/email_signature_generator/EmailSignatureGenerator";
import { ImageToPdfConverter } from "./features/image_to_pdf_converter/ImageToPdfConverter";
import { InvoiceGenerator } from "./features/invoice_generator/InvoiceGenerator";
import { CountdownTimer } from "./features/countdown_timer/CountdownTimer";
import { DateCalculator } from "./features/date_calculator/DateCalculator";
import { CaseConverter } from "./features/case_converter/CaseConverter";
import { UuidGenerator } from "./features/uuid_generator/UuidGenerator";
import { Base64Encoder } from "./features/base64_encoder/Base64Encoder";
import JwtDecoder from "./features/jwt_decoder/JwtDecoder";
import JsonToCSharp from "./features/json_to_csharp/JsonToCSharp";
import { TimezoneConverter } from "./features/timezone_converter/TimezoneConverter";
import { PdfCompression } from "./features/pdf_compression/PdfCompression";

// Supported languages
const supportedLanguages = ['en', 'es', 'fr', 'de', 'pt', 'it', 'ru', 'ja', 'zh', 'ar'];

export function App() {
  const analyticsService = container.resolve(AnalyticsService);
  analyticsService.Initialize("G-VECM5N4S2Q");

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Redirect root to default language */}
        <Route path="/" element={<Navigate to="/en" replace />} />
        
        {/* Language-specific routes */}
        {supportedLanguages.map(lang => (
          <Route key={lang} path={`/${lang}`} element={<Home />} />
        ))}
        
        {/* Tool routes with language prefixes */}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-base-converter`} path={`/${lang}/base-converter`} element={<BaseConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-bmi-calculator`} path={`/${lang}/bmi-calculator`} element={<BmiCalculator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-distance-converter`} path={`/${lang}/distance-converter`} element={<DistanceUnitConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-weight-converter`} path={`/${lang}/weight-converter`} element={<WeightUnitConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-favicon-generator`} path={`/${lang}/favicon-generator`} element={<FaviconGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-temperature-converter`} path={`/${lang}/temperature-converter`} element={<TemperatureConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-interest-calculator`} path={`/${lang}/interest-calculator`} element={<CompoundInterestCalculator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-credit-card-calculator`} path={`/${lang}/credit-card-calculator`} element={<CreditCardRepaymentCalculator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-image-resizer`} path={`/${lang}/image-resizer`} element={<AndroidIosImageResizer />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-pdf-merger`} path={`/${lang}/pdf-merger`} element={<PdfMerger />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-about`} path={`/${lang}/about`} element={<AboutUs />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-image-converter`} path={`/${lang}/image-converter`} element={<ImageConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-compression`} path={`/${lang}/compression`} element={<Compression />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-qr-generator`} path={`/${lang}/qr-generator`} element={<QrCodeGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-password-generator`} path={`/${lang}/password-generator`} element={<PasswordGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-lorem-ipsum`} path={`/${lang}/lorem-ipsum`} element={<LoremIpsumGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-barcode-generator`} path={`/${lang}/barcode-generator`} element={<BarcodeGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-csv-json-converter`} path={`/${lang}/csv-json-converter`} element={<CsvJsonConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-image-cropper`} path={`/${lang}/image-cropper`} element={<ImageCropper />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-email-signature`} path={`/${lang}/email-signature`} element={<EmailSignatureGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-image-to-pdf`} path={`/${lang}/image-to-pdf`} element={<ImageToPdfConverter />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-invoice-generator`} path={`/${lang}/invoice-generator`} element={<InvoiceGenerator />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-countdown-timer`} path={`/${lang}/countdown-timer`} element={<CountdownTimer />} />
        ))}
                 {supportedLanguages.map(lang => (
           <Route key={`${lang}-date-calculator`} path={`/${lang}/date-calculator`} element={<DateCalculator />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-case-converter`} path={`/${lang}/case-converter`} element={<CaseConverter />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-uuid-generator`} path={`/${lang}/uuid-generator`} element={<UuidGenerator />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-base64-encoder`} path={`/${lang}/base64-encoder`} element={<Base64Encoder />} />
         ))}
                   {supportedLanguages.map(lang => (
            <Route key={`${lang}-jwt-decoder`} path={`/${lang}/jwt-decoder`} element={<JwtDecoder />} />
          ))}

          {supportedLanguages.map(lang => (
            <Route key={`${lang}-json-to-csharp`} path={`/${lang}/json-to-csharp`} element={<JsonToCSharp />} />
          ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-timezone-converter`} path={`/${lang}/timezone-converter`} element={<TimezoneConverter />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-pdf-compression`} path={`/${lang}/pdf-compression`} element={<PdfCompression />} />
         ))}

        {/* Dashless redirect routes for backwards compatibility */}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-baseconverter-redirect`} path={`/${lang}/baseconverter`} element={<Navigate to={`/${lang}/base-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-bmicalculator-redirect`} path={`/${lang}/bmicalculator`} element={<Navigate to={`/${lang}/bmi-calculator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-distanceconverter-redirect`} path={`/${lang}/distanceconverter`} element={<Navigate to={`/${lang}/distance-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-weightconverter-redirect`} path={`/${lang}/weightconverter`} element={<Navigate to={`/${lang}/weight-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-favicongenerator-redirect`} path={`/${lang}/favicongenerator`} element={<Navigate to={`/${lang}/favicon-generator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-temperatureconverter-redirect`} path={`/${lang}/temperatureconverter`} element={<Navigate to={`/${lang}/temperature-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-interestcalculator-redirect`} path={`/${lang}/interestcalculator`} element={<Navigate to={`/${lang}/interest-calculator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-creditcardcalculator-redirect`} path={`/${lang}/creditcardcalculator`} element={<Navigate to={`/${lang}/credit-card-calculator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-imageresizer-redirect`} path={`/${lang}/imageresizer`} element={<Navigate to={`/${lang}/image-resizer`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-pdfmerger-redirect`} path={`/${lang}/pdfmerger`} element={<Navigate to={`/${lang}/pdf-merger`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-imageconverter-redirect`} path={`/${lang}/imageconverter`} element={<Navigate to={`/${lang}/image-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-qrgenerator-redirect`} path={`/${lang}/qrgenerator`} element={<Navigate to={`/${lang}/qr-generator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-passwordgenerator-redirect`} path={`/${lang}/passwordgenerator`} element={<Navigate to={`/${lang}/password-generator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-loremipsum-redirect`} path={`/${lang}/loremipsum`} element={<Navigate to={`/${lang}/lorem-ipsum`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-barcodegenerator-redirect`} path={`/${lang}/barcodegenerator`} element={<Navigate to={`/${lang}/barcode-generator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-csvjsonconverter-redirect`} path={`/${lang}/csvjsonconverter`} element={<Navigate to={`/${lang}/csv-json-converter`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-imagecropper-redirect`} path={`/${lang}/imagecropper`} element={<Navigate to={`/${lang}/image-cropper`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-emailsignature-redirect`} path={`/${lang}/emailsignature`} element={<Navigate to={`/${lang}/email-signature`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-imagetopdf-redirect`} path={`/${lang}/imagetopdf`} element={<Navigate to={`/${lang}/image-to-pdf`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-invoicegenerator-redirect`} path={`/${lang}/invoicegenerator`} element={<Navigate to={`/${lang}/invoice-generator`} replace />} />
        ))}
        {supportedLanguages.map(lang => (
          <Route key={`${lang}-countdowntimer-redirect`} path={`/${lang}/countdowntimer`} element={<Navigate to={`/${lang}/countdown-timer`} replace />} />
        ))}
                 {supportedLanguages.map(lang => (
           <Route key={`${lang}-datecalculator-redirect`} path={`/${lang}/datecalculator`} element={<Navigate to={`/${lang}/date-calculator`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-caseconverter-redirect`} path={`/${lang}/caseconverter`} element={<Navigate to={`/${lang}/case-converter`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-uuidgenerator-redirect`} path={`/${lang}/uuidgenerator`} element={<Navigate to={`/${lang}/uuid-generator`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-base64encoder-redirect`} path={`/${lang}/base64encoder`} element={<Navigate to={`/${lang}/base64-encoder`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-jwtdecoder-redirect`} path={`/${lang}/jwtdecoder`} element={<Navigate to={`/${lang}/jwt-decoder`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-timezoneconverter-redirect`} path={`/${lang}/timezoneconverter`} element={<Navigate to={`/${lang}/timezone-converter`} replace />} />
         ))}
         {supportedLanguages.map(lang => (
           <Route key={`${lang}-pdfcompression-redirect`} path={`/${lang}/pdfcompression`} element={<Navigate to={`/${lang}/pdf-compression`} replace />} />
         ))}
 
        {/* Legacy route redirects for SEO */}
        <Route path="/BaseConverter" element={<Navigate to="/en/base-converter" replace />} />
        <Route path="/BmiCalculator" element={<Navigate to="/en/bmi-calculator" replace />} />
        <Route path="/DistanceUnitConverter" element={<Navigate to="/en/distance-converter" replace />} />
        <Route path="/WeightUnitConverter" element={<Navigate to="/en/weight-converter" replace />} />
        <Route path="/FaviconGenerator" element={<Navigate to="/en/favicon-generator" replace />} />
        <Route path="/TemperatureConverter" element={<Navigate to="/en/temperature-converter" replace />} />
        <Route path="/CompoundInterestCalculator" element={<Navigate to="/en/interest-calculator" replace />} />
        <Route path="/CreditCardRepaymentCalculator" element={<Navigate to="/en/credit-card-calculator" replace />} />
        <Route path="/AndroidIosImageResizer" element={<Navigate to="/en/image-resizer" replace />} />
        <Route path="/PdfMerger" element={<Navigate to="/en/pdf-merger" replace />} />
        <Route path="/AboutUs" element={<Navigate to="/en/about" replace />} />
        <Route path="/ImageConverter" element={<Navigate to="/en/image-converter" replace />} />
        <Route path="/Compression" element={<Navigate to="/en/compression" replace />} />
        <Route path="/QrCodeGenerator" element={<Navigate to="/en/qr-generator" replace />} />
        <Route path="/PasswordGenerator" element={<Navigate to="/en/password-generator" replace />} />
        <Route path="/LoremIpsumGenerator" element={<Navigate to="/en/lorem-ipsum" replace />} />
        <Route path="/BarcodeGenerator" element={<Navigate to="/en/barcode-generator" replace />} />
        <Route path="/CsvJsonConverter" element={<Navigate to="/en/csv-json-converter" replace />} />
        <Route path="/ImageCropper" element={<Navigate to="/en/image-cropper" replace />} />
        <Route path="/EmailSignatureGenerator" element={<Navigate to="/en/email-signature" replace />} />
        <Route path="/ImageToPdfConverter" element={<Navigate to="/en/image-to-pdf" replace />} />
        <Route path="/InvoiceGenerator" element={<Navigate to="/en/invoice-generator" replace />} />
        <Route path="/CountdownTimer" element={<Navigate to="/en/countdown-timer" replace />} />
                 <Route path="/DateCalculator" element={<Navigate to="/en/date-calculator" replace />} />
         <Route path="/CaseConverter" element={<Navigate to="/en/case-converter" replace />} />
         <Route path="/UuidGenerator" element={<Navigate to="/en/uuid-generator" replace />} />
         <Route path="/Base64Encoder" element={<Navigate to="/en/base64-encoder" replace />} />
         <Route path="/JwtDecoder" element={<Navigate to="/en/jwt-decoder" replace />} />
         <Route path="/TimezoneConverter" element={<Navigate to="/en/timezone-converter" replace />} />
         <Route path="/PdfCompression" element={<Navigate to="/en/pdf-compression" replace />} />
      </>
    )
  );

  return (
    <Container>
      <RouterProvider router={router} />
    </Container>
  );
}

export default App;
